import React from 'react';
import DownloadTile from '../../components/download-tile/download-tile';
import LongText from '../../components/long-text/long-text';
import styled from '@emotion/styled/macro';
import vars from '../../variables';
import { defineResponsiveGridColumns } from '../../utils/utils';
import { FeaturedDownloadBlockProps } from './featured-download-block.types';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { H2Atom, PAtom } from '../../atoms/text-atoms/text-atoms';
import { sectionTitleBorder, getImageParameters } from '../../mixins';

const FeaturedDownloadBlock: React.FC<FeaturedDownloadBlockProps> = ({
  title,
  anchorId,
  description,
  featuredBackgroundImageSrc = vars.default10ColPlaceholder,
  gatsbyBackgroundImageData,
  featuredBackgroundImageAlt,
  featuredBackgroundImageFormat,
  downloadTileData,
  colour = 'vars.coreDarkGrey',
  className,
}) => {
  if (downloadTileData) {
    const gatsbyImage = getImage(gatsbyBackgroundImageData);
    return (
      <div id={anchorId} className={className}>
        <div className="component-grid-structure">
          <div className="header" data-testid="header">
            <div className="title-container">
              <H2Atom data-testid="title">{title}</H2Atom>
            </div>
            <LongText content={description} />
          </div>
          <div className="content" data-testid="content">
            <div className="tile-wrapper">
              <DownloadTile
                downloadType={downloadTileData.type}
                title={downloadTileData?.title}
                downloadSrc={downloadTileData.downloadSrc}
                availableInMultipleLocales={
                  downloadTileData.availableInMultipleLocales
                }
                enableLightText={downloadTileData.enableLightText}
                backgroundImageSrc={
                  downloadTileData?.image && downloadTileData.image.file
                    ? downloadTileData.image.file.url
                    : undefined
                }
                gatsbyImageData={downloadTileData.image.gatsbyImageData}
                imageAlt={
                  downloadTileData.image && downloadTileData.image.file
                    ? downloadTileData.image.description
                    : undefined
                }
                imageFormat={
                  downloadTileData.image && downloadTileData.image.file
                    ? downloadTileData.image.file.contentType
                    : undefined
                }
                backgroundColour={downloadTileData.backgroundColour}
              />
            </div>
            <div className="image" data-testid="image-container">
              {gatsbyImage &&
              featuredBackgroundImageFormat !== 'image/gif' &&
              featuredBackgroundImageFormat !== 'image/svg+xml' ? (
                <GatsbyImage
                  image={gatsbyImage}
                  alt={
                    featuredBackgroundImageAlt ? featuredBackgroundImageAlt : ''
                  }
                  className="gatsby-image-pos"
                />
              ) : (
                <div className="gatsby-image-wrapper gatsby-image-wrapper-constrained gatsby-image-pos">
                  {featuredBackgroundImageSrc && (
                    <img
                      src={getImageParameters(
                        featuredBackgroundImageSrc,
                        vars.defaultImageFormat,
                        1392,
                        750,
                        vars.defaultImageFit,
                        vars.defaultImageFocus,
                        50
                      )}
                      alt={
                        featuredBackgroundImageAlt
                          ? featuredBackgroundImageAlt
                          : ''
                      }
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
  console.error(
    `Download tile called with invalid tile data: ${downloadTileData}`
  );
  return null;
};

const StyledFeaturedDownloadBlock = styled(FeaturedDownloadBlock)`
max-width: ${vars.mainMaxWidth};
margin-top: ${({ anchorId }) => (anchorId ? vars.spacingL : 'auto')};
margin: 0 auto;

.component-grid-structure {
  
  @media screen and (min-width: ${vars.breakPointTenColumn}) {
    display: grid;
    grid-template-rows: [row1-start] auto [row1-end row2-start] auto [row2-end];
    column-gap: ${vars.standardColumnPercentageGap};
    grid-template-columns: ${defineResponsiveGridColumns(12)};
  }

.header {
  @media screen and (min-width: ${vars.breakPointTenColumn}) {
    display: grid;
    grid-column: col4-start / col9-end;
    grid-row: row1-start / row1-end;
    .title-container {
      margin-bottom: 1.2rem;
      ${H2Atom} {
        ${vars.fontSize8}
        ${({ colour }) => sectionTitleBorder(colour)};
        padding-top: 0;
        padding-bottom: 0;
      }
    }
  }
  ${PAtom} {
    @media screen and (min-width: ${vars.breakPointTenColumn}) {
      ${vars.fontSize4}
      line-height: 1.4;
      padding-bottom: 1.8rem;
    }
  }
}

.content {
  position: relative;
  margin-top: 2rem;
  padding-bottom: 4.8rem;
  overflow: hidden;
  .image {
    position: absolute;
    display: block;
    top: 0;
    width: 100%;
    height: 37.5rem;
    overflow: hidden;
    z-index: -10;
    border-radius: 8px;
  }
  .gatsby-image-pos {
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
  @media screen and (min-width: ${vars.breakPointTenColumn}) {
    grid-column: col3-start/ col10-end;
    grid-row: row2-start / row2-end;
    display: flex;
    .image {
      position: relative;
      display: flex;
      height: 32rem;
    }
  }
  @media screen and (min-width: ${vars.breakPointTwelveColumn}) {
    .image {
      height: 37.5rem;
    }
  }
  .tile-wrapper {
    z-index: 200;
    display: flex;
    align-items: center;
    justify-content: center;
    padding-top: 4.8rem;
    @media screen and (min-width: ${vars.breakPointTenColumn}) {
      margin-right: -16.4rem;
      padding-top: 2rem;
      padding-bottom: 2rem;
    }
    @media screen and (min-width: ${vars.breakPointTwelveColumn}) {
      display: block;
      margin-right: -10.4rem;
      padding-top: 4.8rem;
      padding-bottom: 0;
    }
  }
  @media screen and (min-width: ${vars.breakPointTenColumn}) {
      padding-bottom: 2rem;
    }
  @media screen and (min-width: ${vars.breakPointTwelveColumn}) {
    padding-bottom: 4.8rem;
  }
}
`;

export default StyledFeaturedDownloadBlock;
